import {
  AppLayout,
  AppLayoutProps,
  Box,
  BreadcrumbGroup,
  Button,
  Container,
  DatePicker,
  Flashbar,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Spinner,
  Table,
  TableProps,
  Tabs,
  TimeInput,
  Toggle
} from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FinanceCalendarEventEntity } from 'src/api/interfaces';
import FinanceCalendarContent from 'src/components/dfp-home-page/landing-page/due-this-week/FinanceCalendarContent';
import {
  FINANCE_CALENDAR_SCENARIOS,
  toFinanceCalendarWidgetData
} from 'src/components/dfp-home-page/landing-page/due-this-week/utils/financeCalendarTransformers';
import { ErrorFallback } from 'src/components/generic-components/ErrorFallback';
import { appLayoutAriaLabels } from 'src/i18n-strings';
import { AdminSideNavigationV2 } from '../AdminSideNavigationV2';
import { EventValidationErrors, useFinanceCalendarManagement } from './useFinanceCalendarManagement';

const scenarioOptions = [{ label: '-', value: '-' }, ...FINANCE_CALENDAR_SCENARIOS.map((s) => ({ label: s, value: s }))];

const scenarioFilterTabs = [
  { id: 'ALL', label: 'All' },
  ...FINANCE_CALENDAR_SCENARIOS.map((s) => ({ id: s, label: s })),
  { id: '-', label: '- (unset)' }
];

const PREVIEW_DEBOUNCE_MS = 400;

// ── Memoized cell components to prevent full-table re-renders ──

interface CellProps {
  item: FinanceCalendarEventEntity;
  onUpdate: (eventId: string, field: keyof FinanceCalendarEventEntity, value: string) => void;
  error?: string;
}

const ScenarioCell = React.memo<CellProps>(({ item, onUpdate, error }) => (
  <FormField errorText={error}>
    <Select
      selectedOption={scenarioOptions.find((o) => o.value === item.scenario) || null}
      onChange={({ detail }) => onUpdate(item.eventId, 'scenario', detail.selectedOption.value || '')}
      options={scenarioOptions}
      expandToViewport
      invalid={!!error}
    />
  </FormField>
));

const DateCell = React.memo<CellProps>(({ item, onUpdate, error }) => {
  const [datePart] = (item.dateTime || '').split('T');
  return (
    <FormField errorText={error}>
      <DatePicker
        value={datePart || ''}
        onChange={({ detail }) => {
          const [, timePart] = (item.dateTime || '').split('T');
          onUpdate(item.eventId, 'dateTime', `${detail.value}T${timePart || '00:00:00'}`);
        }}
        placeholder="YYYY-MM-DD"
        expandToViewport
        invalid={!!error}
      />
    </FormField>
  );
});

const TimeCell = React.memo<{ item: FinanceCalendarEventEntity; onUpdate: CellProps['onUpdate'] }>(({ item, onUpdate }) => {
  const [, timePart] = (item.dateTime || '').split('T');
  return (
    <TimeInput
      value={timePart || '00:00:00'}
      onChange={({ detail }) => {
        const [datePart] = (item.dateTime || '').split('T');
        onUpdate(item.eventId, 'dateTime', `${datePart || ''}T${detail.value}`);
      }}
      format="hh:mm:ss"
      placeholder="HH:MM:SS"
    />
  );
});

const EventCell = React.memo<CellProps>(({ item, onUpdate, error }) => (
  <FormField errorText={error}>
    <Input
      value={item.event}
      onChange={({ detail }) => onUpdate(item.eventId, 'event', detail.value.slice(0, 120))}
      placeholder="e.g., 1st Pass: GP P&L Snapshot"
      invalid={!!error}
    />
  </FormField>
));

const DescriptionCell = React.memo<{ item: FinanceCalendarEventEntity; onUpdate: CellProps['onUpdate'] }>(({ item, onUpdate }) => (
  <Input
    value={item.description}
    onChange={({ detail }) => onUpdate(item.eventId, 'description', detail.value.slice(0, 120))}
    placeholder="e.g., 1st Pass: GP P&L snapshot in Prophecy"
  />
));

// ── Debounce hook ──

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timerRef.current = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timerRef.current);
  }, [value, delayMs]);

  return debounced;
}

// ── Main component ──

export const FinanceCalendarManagement: React.FC = () => {
  const {
    events,
    loading,
    saving,
    isDirty,
    flashbarItems,
    validationErrors,
    showValidation,
    selectedEvents,
    setSelectedEvents,
    handleAddBlankEvent,
    handleUpdateEventField,
    handleDeleteEvents,
    handleSave
  } = useFinanceCalendarManagement();

  // Preview / Edit mode toggle
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Scenario filter
  const [filterScenario, setFilterScenario] = useState<string>('ALL');
  const filteredEvents = useMemo(
    () => (filterScenario === 'ALL' ? events : events.filter((e) => e.scenario === filterScenario)),
    [events, filterScenario]
  );

  // Debounce the events used for preview so it doesn't recompute on every keystroke
  const debouncedEvents = useDebouncedValue(events, PREVIEW_DEBOUNCE_MS);
  const previewData = useMemo(() => toFinanceCalendarWidgetData(debouncedEvents), [debouncedEvents]);

  /** Get error text for a field if validation is being shown */
  const getFieldError = useCallback(
    (eventId: string, field: keyof EventValidationErrors): string | undefined => {
      if (!showValidation) return undefined;
      return validationErrors.get(eventId)?.[field];
    },
    [showValidation, validationErrors]
  );

  const columnDefinitions: TableProps.ColumnDefinition<FinanceCalendarEventEntity>[] = useMemo(
    () => [
      {
        id: 'scenario',
        header: 'Scenario',
        width: 170,
        cell: (item: FinanceCalendarEventEntity) => (
          <ScenarioCell item={item} onUpdate={handleUpdateEventField} error={getFieldError(item.eventId, 'scenario')} />
        )
      },
      {
        id: 'date',
        header: 'Date',
        width: 310,
        cell: (item: FinanceCalendarEventEntity) => (
          <DateCell item={item} onUpdate={handleUpdateEventField} error={getFieldError(item.eventId, 'date')} />
        )
      },
      {
        id: 'time',
        header: 'Time',
        width: 140,
        cell: (item: FinanceCalendarEventEntity) => <TimeCell item={item} onUpdate={handleUpdateEventField} />
      },
      {
        id: 'event',
        header: 'Event',
        cell: (item: FinanceCalendarEventEntity) => (
          <EventCell item={item} onUpdate={handleUpdateEventField} error={getFieldError(item.eventId, 'event')} />
        )
      },
      {
        id: 'description',
        header: 'Description',
        cell: (item: FinanceCalendarEventEntity) => <DescriptionCell item={item} onUpdate={handleUpdateEventField} />
      },
      {
        id: 'source',
        header: 'Source',
        width: 100,
        cell: (item: FinanceCalendarEventEntity) => item.source || 'manual'
      }
    ],
    [handleUpdateEventField, getFieldError]
  );

  const renderContent = (): JSX.Element => {
    if (loading) {
      return (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
        </Box>
      );
    }

    const sharedHeader = (
      <Header
        variant="h2"
        counter={!isPreviewMode ? (filterScenario === 'ALL' ? `(${events.length})` : `(${filteredEvents.length} of ${events.length})`) : undefined}
        actions={
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            {!isPreviewMode && (
              <>
                <Button iconName="add-plus" onClick={handleAddBlankEvent}>
                  Add event
                </Button>
                <Button onClick={handleDeleteEvents} disabled={selectedEvents.length === 0}>
                  Delete
                </Button>
                <Button variant="primary" onClick={handleSave} loading={saving} disabled={!isDirty}>
                  Save Changes
                </Button>
              </>
            )}
            <Toggle checked={isPreviewMode} onChange={({ detail }) => setIsPreviewMode(detail.checked)}>
              Preview
            </Toggle>
          </SpaceBetween>
        }
      >
        Finance Calendar Events
      </Header>
    );

    return (
      <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
        {isPreviewMode ? (
          <Container header={sharedHeader}>
            <FinanceCalendarContent status="finished" data={previewData} />
          </Container>
        ) : (
          <Table
            columnDefinitions={columnDefinitions}
            items={filteredEvents}
            selectionType="multi"
            selectedItems={selectedEvents}
            onSelectionChange={({ detail }) => setSelectedEvents(detail.selectedItems)}
            trackBy="eventId"
            filter={
              <Tabs
                tabs={scenarioFilterTabs}
                activeTabId={filterScenario}
                onChange={({ detail }) => {
                  setFilterScenario(detail.activeTabId);
                  setSelectedEvents([]);
                }}
                disableContentPaddings
              />
            }
            empty={
              <Box textAlign="center" color="inherit">
                <b>No Calendar Events</b>
                <Box variant="p" color="inherit">
                  {filterScenario !== 'ALL'
                    ? `No events match the "${filterScenario}" filter. Try selecting "All scenarios".`
                    : 'Click "Add event" to create a new finance calendar event.'}
                </Box>
              </Box>
            }
            header={sharedHeader}
          />
        )}
      </ErrorBoundary>
    );
  };

  const appLayoutProps: AppLayoutProps = {
    ariaLabels: appLayoutAriaLabels,
    navigation: <AdminSideNavigationV2 />,
    stickyNotifications: true,
    notifications: <Flashbar items={flashbarItems} />,
    toolsHide: true,
    contentType: 'default',
    content: renderContent()
  };

  return <AppLayout {...appLayoutProps} />;
};
